import { z } from "zod";

export enum ProfileProgramMembership {
  A1C_ONLY = "A1C_ONLY",
  MEDICARE = "MEDICARE",
  CDCDPP = "CDCDPP",
  ALLINONE_HEALTHCARE_CONCIERGE = "ALLINONE_HEALTHCARE_CONCIERGE",
  HEALTHY_WEIGHT_JOURNEY = "HEALTHY_WEIGHT_JOURNEY",
  DIABETES_AND_HEART_DISEASE_PREVENTION = "DIABETES_AND_HEART_DISEASE_PREVENTION",
  EVERYDAY_DIABETES_JOURNEY = "EVERYDAY_DIABETES_JOURNEY"
}

/**
 * Mirrors the backend `MedicalConditions` enum
 * (`profile/domain/MedicalConditions.java`) — the values are the exact
 * `condition.name` strings the profile API returns.
 *
 * Names must match the backend character for character: the feed request schema
 * drops condition values it doesn't recognise (see `feed.ts`), so a missing or
 * misspelled entry silently strips the condition from the member's profile.
 * ICD-10 codes are the primary matching signal, but names remain the fallback
 * for the 20 backend conditions that carry no diagnosis code.
 *
 * Keep this a complete mirror, including conditions the feed never branches on.
 * The app reports profile conditions missing from this enum to Sentry, so
 * leaving known values out turns that signal into noise.
 *
 * `currentlyOnGlp1` is not a `MedicalConditions` name at all — it is a
 * `ConditionGroup` name, kept here because the feed's `primaryCondition` field
 * is typed against this enum. See `docs/dictionary.md` → "Condition Group".
 */
export enum Condition {
  diabetesType1 = "Diabetes type 1",
  diabetesType2 = "Diabetes type 2",
  prediabetes = "Prediabetes",
  gestationalDiabetes = "Gestational diabetes",
  unknownDiabetes = "Diabetes - type unknown",
  diabetesNotDiagnosed = "Diabetes - not diagnosed",
  diabetesType1WithHighA1c = "Diabetes type 1 with high HbA1c",
  diabetesType2WithHighA1c = "Diabetes type 2 with high HbA1c",
  unknownDiabetesWithHighA1c = "Diabetes - type unknown with high HbA1c",
  highBloodPressure = "High blood pressure",
  elevatedBloodPressureWithoutHypertensionDiagnosis = "Elevated blood-pressure reading, without diagnosis of hypertension",
  highColesterol = "High cholesterol",
  kidneyOrRenalDisease = "Kidney or renal disease",
  kidneyDiseaseOrImpairment = "Chronic kidney disease or kidney impairment",
  kidneyDiseaseEndStageOrDialysis = "End-stage kidney or renal disease, or receiving dialysis",
  liverOrHepaticDisease = "Liver or hepatic disease",
  liverFailureOrCirrhosis = "Liver failure or cirrhosis",
  stroke = "Stroke",
  congestiveHeartFailure = "Congestive heart failure (CHF)",
  congestiveHeartFailureClassIIIOrIV = "Congestive heart failure (CHF) Class III or IV",
  coronaryArteryDisease = "Coronary artery disease (CAD)",
  peripheralArterialDisease = "Peripheral arterial disease (PAD)",
  acuteOrChronicMetabolicAcidosis = "Acute or chronic metabolic acidosis",
  obstructiveSleepApnea = "Obstructive Sleep Apnea",
  nonAlcoholicFattyLiverDisease = "Non-alcoholic fatty liver disease (NAFLD or NASH)",
  pmosPolyendocrineMetabolicOvarianSyndrome = "Polyendocrine Metabolic Ovarian Syndrome (PMOS, formerly PCOS)",
  gallbladderDisease = "Gallbladder disease (gallstones, inflamed or infected gallbladder)",
  medullaryThyroidCancer = "History of Medullary Thyroid Cancer or Multiple Endocrine Neoplasia syndrome",
  severeDiabeticGastroparesis = "Severe diabetic gastroparesis",
  pancreatitisOrTumors = "History of pancreatitis or tumors of the pancreas",
  obesity = "Obesity (BMI of 30 or greater)",
  organTransplant = "Organ transplant received (i.e. kidney, liver, heart, lung)",
  activeCancer = "Active cancer",
  heartAttackStrokeSurgeryInPast90Days = "Heart attack, stroke, or major surgery within the past 90 days",
  fattyLiverDiseaseNafldNashMasld = "Fatty liver disease (NAFLD, NASH, or MASLD)",
  osteoarthritis = "Osteoarthritis",
  gerdRequiringMedications = "Gastroesophageal reflux disease (GERD) requiring medications",
  obesityHypoventilationSyndrome = "Obesity hypoventilation syndrome",
  pseudotumorCerebri = "Pseudotumor Cerebri (Idiopathic Intracranial Hypertension)",
  lowTestosterone = "Low Testosterone (Male Hypogonadism)",

  overweight = "Overweight",
  obesityDueToExcessCalories = "Obesity due to excess calories",
  bmi19_9OrLess = "Body mass index [BMI] 19.9 or less, adult",
  bmi20To29 = "Body mass index [BMI] 20-29, adult",
  bmi20_0To20_9 = "Body mass index [BMI] 20.0-20.9, adult",
  bmi21_0To21_9 = "Body mass index [BMI] 21.0-21.9, adult",
  bmi22_0To22_9 = "Body mass index [BMI] 22.0-22.9, adult",
  bmi23_0To23_9 = "Body mass index [BMI] 23.0-23.9, adult",
  bmi24_0To24_9 = "Body mass index [BMI] 24.0-24.9, adult",
  bmi25_0To25_9 = "Body mass index (BMI) 25.0-25.9, adult",
  bmi26_0To26_9 = "Body mass index [BMI] 26.0-26.9, adult",
  bmi27_0To27_9 = "Body mass index [BMI] 27.0-27.9, adult",
  bmi28_0To28_9 = "Body mass index [BMI] 28.0-28.9, adult",
  bmi29_0To29_9 = "Body mass index [BMI] 29.0-29.9, adult",
  bmi30_0To30_9 = "Body mass index [BMI] 30.0-30.9, adult",
  bmi31_0To31_9 = "Body mass index [BMI] 31.0-31.9, adult",
  bmi32_0To32_9 = "Body mass index [BMI] 32.0-32.9, adult",
  bmi33_0To33_9 = "Body mass index [BMI] 33.0-33.9, adult",
  bmi34_0To34_9 = "Body mass index [BMI] 34.0-34.9, adult",
  bmi35_0To35_9 = "Body mass index [BMI] 35.0-35.9, adult",
  bmi36_0To36_9 = "Body mass index [BMI] 36.0-36.9, adult",
  bmi37_0To37_9 = "Body mass index [BMI] 37.0-37.9, adult",
  bmi38_0To38_9 = "Body mass index [BMI] 38.0-38.9, adult",
  bmi39_0To39_9 = "Body mass index [BMI] 39.0-39.9, adult",
  bmi40_0To44_9 = "Body mass index [BMI] 40.0-44.9, adult",
  bmi45_0To49_9 = "Body mass index [BMI] 45.0-49.9, adult",
  bmi50_0To59_9 = "Body mass index [BMI] 50.0-59.9, adult",
  bmi60_0To69_9 = "Body mass index [BMI] 60.0-69.9, adult",
  bmi70OrGreater = "Body mass index [BMI] 70 or greater, adult",

  currentlyOnGlp1 = "Currently on GLP1"
}

/**
 * Conditions that qualify a member for the diabetes content category.
 *
 * Mirrors the backend `MedicalConditionAttribute.DIABETES_RELATED_CONDITIONS`
 * (which includes prediabetes and the undiagnosed/HbA1c-derived variants), plus
 * `severeDiabeticGastroparesis` — a diabetes complication the backend groups
 * separately but which we treat as diabetes for content purposes, and which
 * carries no ICD-10 code, so only a name can match it.
 */
export const diabetesConditions: ReadonlySet<Condition> = new Set([
  Condition.diabetesType1,
  Condition.diabetesType2,
  Condition.prediabetes,
  Condition.gestationalDiabetes,
  Condition.unknownDiabetes,
  Condition.diabetesNotDiagnosed,
  Condition.diabetesType1WithHighA1c,
  Condition.diabetesType2WithHighA1c,
  Condition.unknownDiabetesWithHighA1c,
  Condition.severeDiabeticGastroparesis
]);

/**
 * Conditions that qualify a member for the hypertension content category.
 *
 * Includes the observation-derived elevated-reading condition: the backend
 * assigns it to members whose blood pressure is above 130/80 but who have no
 * hypertension diagnosis, and they are the audience for this content.
 */
export const hypertensionConditions: ReadonlySet<Condition> = new Set([
  Condition.highBloodPressure,
  Condition.elevatedBloodPressureWithoutHypertensionDiagnosis
]);

/**
 * ICD-10 families that qualify a member for the diabetes content category.
 *
 * Codes are the stable half of the profile's condition data — a condition's
 * display name is prose that gets reworded, but `E11` stays type-2 diabetes.
 * Matching on the family also covers conditions the backend adds later
 * (any new `E11.x` variant) and diagnoses that arrive from claims/EHR rather
 * than from our own `Default9AmDiagnosis` list, neither of which name matching
 * can handle.
 *
 * `E10`/`E11` = type 1 / type 2 diabetes mellitus, `O24` = diabetes in
 * pregnancy, `R73.03` = prediabetes, `Z13.1` = screening for diabetes
 * (the backend's "Diabetes - not diagnosed").
 */
export const diabetesIcd10Families = ["E10", "E11", "O24", "R73.03", "Z13.1"];

/**
 * ICD-10 families that qualify a member for the hypertension content category.
 * `I10` = essential hypertension, `R03.0` = elevated reading without a
 * diagnosis.
 *
 * The wider hypertensive-disease block (`I11`–`I16`, hypertensive heart/kidney
 * disease) is deliberately excluded — the backend maps none of its conditions
 * to those codes, so including them would be a product decision about who sees
 * hypertension content, not a bug fix.
 */
export const hypertensionIcd10Families = ["I10", "R03.0"];

/**
 * True when an ICD-10 code belongs to one of the given families.
 *
 * Compares on code boundaries rather than a raw `startsWith`, so the family
 * `E1` could never swallow `E10`–`E19` and `I10` never matches `I100`.
 */
export const isIcd10InFamilies = (
  code: string,
  families: readonly string[]
): boolean => {
  const normalized = code.trim().toUpperCase();
  return families.some(
    (family) => normalized === family || normalized.startsWith(`${family}.`)
  );
};

export enum Payer {
  Evernorth = "Evernorth"
}

export enum Employer {
  ThermoFisher = "Thermo Fisher",
  HarvardUniversity = "Harvard University",
  BankOfNewYork = "Bank of New York"
}

export const ProfileProgramMembershipSchema = z.enum(ProfileProgramMembership);
export const ConditionSchema = z.enum(Condition);
export const EmployerSchema = z.enum(Employer);

export enum JourneyName {
  default = "Default"
}

export enum MedicalCategory {
  diabetes = "diabetes",
  hypertension = "hypertension",
  weightLoss = "weight-loss"
}

export enum DefaultCategory {
  all = "all",
  nutrition = "nutrition",
  fitness = "fitness",
  mentalHealth = "mental-health",
  heartHealth = "heart-health"
}

export type Category = DefaultCategory | MedicalCategory;

export type JourneyKeys = string;

export interface Caption {
  language?: string;
  srt?: {
    url?: string;
    path?: string;
    mimetype?: string;
  };
}

export interface EducationalContentResponseCategory {
  title: string;
  id: string;
  slug: string;
}

export interface EducationalContentResponseImage {
  metadata: {
    lqip: string;
    dimensions: {
      width: number;
      height: number;
      aspectRatio: number;
    };
  };
  path: string;
  url: string;
  size: number;
}

export interface EducationalContentResponseFeedItem {
  id: string;
  title: string;
  type: string;
  description: string;
  availableDate: string;
  categories: EducationalContentResponseCategory[];
  image: EducationalContentResponseImage | null;
  url: string | null;
  tags: string[];
  sanctuaryHealthVideo: null | {
    title: string;
    vimeoId?: string;
    photoUrl?: string;
    approximateTime?: number;
    description?: string;
    landscapeVideo?: {
      path?: string;
      captions?: Caption[];
    };
  };
}

export interface EducationalContentResponseFeed {
  language: string;
  categories: EducationalContentResponseCategory[];
  feed: EducationalContentResponseFeedItem[];
  feedLength: number;
}

export type SanityJourneyTypes = "time-based";

export interface SanityJourneyContentOverview {
  _id: string;
  interval?: number;
  language?: string;
  listOfClasses?: {
    name?: string;
    language: string;
    listOfEducationalContent?: { _ref: string }[];
  }[];
  name?: string;
  type?: SanityJourneyTypes;
}

export interface SanityLessonContent {
  _id: string;
  title?: string;
  type?: string;
  description?: string;
  postUrl?: string;
  image?: {
    metadata: {
      lqip: string;
      dimensions: {
        width: number;
        height: number;
        aspectRatio: number;
      };
    };
    path: string;
    url: string;
    size: number;
  };
  video: null | {
    _id: string;
    title: string;
    vimeoId?: string;
    photoUrl?: string;
    approximateTime?: number;
    description?: string;
    videoUrl: string;
    captions?: Caption[] | [];
  };
  categories?: {
    title: string;
    _id: string;
    slug: string;
  }[];
}

export interface SanityJourneyContentDetails {
  _id: string;
  interval?: number;
  language?: string;
  listOfClassesContentAvailable?: {
    name?: string;
    _id: string;
    _type: string;
    listOfEducationalContent?: SanityLessonContent[];
  }[];
  name?: string;
  type?: SanityJourneyTypes;
}
